/**
 * Stale-build chunk-error classification — the single source of truth used by
 * every surface that can observe one:
 *  - sentry.client.config.ts `beforeSend` (noise backstop, all capture paths)
 *  - useStaleChunkReload (window error / unhandledrejection — dynamic imports)
 *  - GlobalError ErrorBoundary (next/dynamic failures bubbling through render)
 *
 * After a deploy, sessions still on the previous buildId request hashed chunks
 * that no longer exist (404 → "Failed to load script" / ChunkLoadError). These
 * are expected, transient deploy-skew errors: recoverable by a hard reload and
 * non-actionable as Sentry events.
 *
 * Keep this module dependency-free so it is cheap to import from
 * sentry.client.config.ts, which loads before the rest of the app.
 */

const CHUNK_ERROR_MESSAGE =
  /Failed to load (script|static props)|Loading (CSS )?chunk|ChunkLoadError/

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false
  if (typeof error === 'string') return CHUNK_ERROR_MESSAGE.test(error)
  const { name, message } = error as { name?: string; message?: string }
  return name === 'ChunkLoadError' || CHUNK_ERROR_MESSAGE.test(message ?? '')
}

// Sentry hooks window.onerror/onunhandledrejection directly and ignores
// event.preventDefault(), so per-error suppression must go through this flag
// that sentry.client.config.ts beforeSend filters on.
//
// A primitive string (e.g. a cross-origin window 'error' with a null `error`
// and only a `message`) can't carry a flag — that's fine: the central
// isChunkLoadError(originalException) check in beforeSend matches strings too
// and is the real backstop, so this flag is only a fast-path for object errors.
export const markSkipSentry = (error: unknown) => {
  if (error !== null && typeof error === 'object') {
    ;(error as { skipSentry?: boolean }).skipSentry = true
  }
}
